import { Address } from 'viem';

// Otherwise we cannot access process.env var using dynamic name in getEnvVar
const envVars = {
  NEXT_PUBLIC_SFS_BASE_URL: process.env.NEXT_PUBLIC_SFS_BASE_URL,
  NEXT_PUBLIC_BACKEND_API_URL: process.env.NEXT_PUBLIC_BACKEND_API_URL,
  NEXT_PUBLIC_EXPLORER_URL: process.env.NEXT_PUBLIC_EXPLORER_URL,

  NEXT_PUBLIC_TOKEN_DEPOSIT_GAS_LIMIT: process.env.NEXT_PUBLIC_TOKEN_DEPOSIT_GAS_LIMIT,
  NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,

  NEXT_PUBLIC_MAINTENANCE_END_DATE_UTC: process.env.NEXT_PUBLIC_MAINTENANCE_END_DATE_UTC,

  NEXT_PUBLIC_CONTENTFUL_URL: process.env.NEXT_PUBLIC_CONTENTFUL_URL,
  NEXT_PUBLIC_SHOW_CONTENT_LIVE: process.env.NEXT_PUBLIC_SHOW_CONTENT_LIVE,
  NEXT_PUBLIC_CONTENTFUL_SPACE: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE,
  NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT: process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT,
  NEXT_PUBLIC_CONTENTFUL_TOKEN: process.env.NEXT_PUBLIC_CONTENTFUL_TOKEN,
  NEXT_PUBLIC_CONTENTFUL_HOST: process.env.NEXT_PUBLIC_CONTENTFUL_HOST,

  NEXT_PUBLIC_L1_BRIDGE_ADDRESS: process.env.NEXT_PUBLIC_L1_BRIDGE_ADDRESS,
  NEXT_PUBLIC_L1_CHAIN_ID: process.env.NEXT_PUBLIC_L1_CHAIN_ID,
  NEXT_PUBLIC_L1_RPC: process.env.NEXT_PUBLIC_L1_RPC,
  NEXT_PUBLIC_L2_BRIDGE_ADDRESS: process.env.NEXT_PUBLIC_L2_BRIDGE_ADDRESS,
  NEXT_PUBLIC_L2_CHAIN_ID: process.env.NEXT_PUBLIC_L2_CHAIN_ID,
  NEXT_PUBLIC_L2_RPC: process.env.NEXT_PUBLIC_L2_RPC,

  NEXT_PUBLIC_LOCK_ADDRESS: process.env.NEXT_PUBLIC_LOCK_ADDRESS,
  NEXT_PUBLIC_MODE_TOKEN_ADDRESS: process.env.NEXT_PUBLIC_MODE_TOKEN_ADDRESS,
  NEXT_PUBLIC_LOCK_MODE_USDC_BPT_ADDRESS: process.env.NEXT_PUBLIC_LOCK_MODE_USDC_BPT_ADDRESS,
  NEXT_PUBLIC_MODE_USDC_BPT_TOKEN_ADDRESS: process.env.NEXT_PUBLIC_MODE_USDC_BPT_TOKEN_ADDRESS,

  NEXT_PUBLIC_PRIVY_APP_ID: process.env.NEXT_PUBLIC_PRIVY_APP_ID,

  NEXT_PUBLIC_TURNSTILE_SITEKEY: process.env.NEXT_PUBLIC_TURNSTILE_SITEKEY,

  // general
  NEXT_PUBLIC_DEBUG: process.env.NEXT_PUBLIC_DEBUG,

  // terminal
  E2B_API_KEY: process.env.E2B_API_KEY,
  E2B_TEMPLATE_ID: process.env.E2B_TEMPLATE_ID,

  // Synth
  SYNTH_API_URL: process.env.SYNTH_API_URL,
  SYNTH_API_KEY: process.env.SYNTH_API_KEY,
  OPENAI_API_KEY_SYNTH: process.env.OPENAI_API_KEY_SYNTH,
  DEREBIT_API_URL: process.env.DEREBIT_API_URL,

  // Supabase
  NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
  NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,

  // Next
  NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,

  // Google Analytics
  NEXT_PUBLIC_GA_MEASUREMENT_ID: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,

  // Intercom
  NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
};

export const getEnvVar = (name: keyof typeof envVars, defaultValue = '', backend = false): string => {
  if (!name.startsWith('NEXT_PUBLIC_') && !backend) {
    return '';
  }
  const value = envVars[name];
  if (!value) {
    return defaultValue;
  }
  return value;
};

const localConfig = {
  sfsBaseUrl: getEnvVar('NEXT_PUBLIC_SFS_BASE_URL'),
  apiUrl: getEnvVar('NEXT_PUBLIC_BACKEND_API_URL'),
  explorerUrl: getEnvVar('NEXT_PUBLIC_EXPLORER_URL'),

  tokenDepositGasLimit: getEnvVar('NEXT_PUBLIC_TOKEN_DEPOSIT_GAS_LIMIT'),
  posthogHost: getEnvVar('NEXT_PUBLIC_POSTHOG_HOST'),

  contentfulUrl: getEnvVar('NEXT_PUBLIC_CONTENTFUL_URL'),
  showContentLive: getEnvVar('NEXT_PUBLIC_SHOW_CONTENT_LIVE') === 'true',
  contentfulSpace: getEnvVar('NEXT_PUBLIC_CONTENTFUL_SPACE'),
  contentfulEnvironment: getEnvVar('NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT'),
  contentfulToken: getEnvVar('NEXT_PUBLIC_CONTENTFUL_TOKEN'),
  contentfulHost: getEnvVar('NEXT_PUBLIC_CONTENTFUL_HOST'),

  // feature flags
  maintenanceEndDateUtc: getEnvVar('NEXT_PUBLIC_MAINTENANCE_END_DATE_UTC'),

  // L1
  l1BridgeAddress: getEnvVar('NEXT_PUBLIC_L1_BRIDGE_ADDRESS') as Address,
  l1ChainId: getEnvVar('NEXT_PUBLIC_L1_CHAIN_ID'),
  l1Rpc: getEnvVar('NEXT_PUBLIC_L1_RPC'),

  // L2
  l2BridgeAddress: getEnvVar('NEXT_PUBLIC_L2_BRIDGE_ADDRESS') as Address,
  l2ChainId: getEnvVar('NEXT_PUBLIC_L2_CHAIN_ID'),
  l2Rpc: getEnvVar('NEXT_PUBLIC_L2_RPC'),

  // Lock
  lockAddress: getEnvVar('NEXT_PUBLIC_LOCK_ADDRESS') as Address,
  modeTokenAddress: getEnvVar('NEXT_PUBLIC_MODE_TOKEN_ADDRESS') as Address,

  lockModeUsdcBptAddress: getEnvVar('NEXT_PUBLIC_LOCK_MODE_USDC_BPT_ADDRESS') as Address,
  modeModeUsdcBptTokenAddress: getEnvVar('NEXT_PUBLIC_MODE_USDC_BPT_TOKEN_ADDRESS') as Address,

  allowedPromptsCount: process.env.NEXT_PUBLIC_ALLOWED_PROMPTS_COUNT
    ? parseInt(process.env.NEXT_PUBLIC_ALLOWED_PROMPTS_COUNT)
    : 3,

  privyAppId: getEnvVar('NEXT_PUBLIC_PRIVY_APP_ID'),
  turnstileSitekey: getEnvVar('NEXT_PUBLIC_TURNSTILE_SITEKEY'),

  // Trading agents
  positionMinAllowedSizeUSDC: process.env.NEXT_PUBLIC_POSITION_MIN_ALLOWED_SIZE_USDC
    ? parseInt(process.env.NEXT_PUBLIC_POSITION_MIN_ALLOWED_SIZE_USDC)
    : 10,
  positionMaxAllowedSizeUSDC: process.env.NEXT_PUBLIC_POSITION_MAX_ALLOWED_SIZE_USDC
    ? parseInt(process.env.NEXT_PUBLIC_POSITION_MAX_ALLOWED_SIZE_USDC)
    : 50000,
};

export default localConfig;
